import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../api';
import { NutzungHinweis } from '../billtoro/NutzungHinweis';
import { fehlendeFunktion, upgradeAnbieten } from '../billtoro/upgrade';
import { fmtDate, MONTHS, MONTHS_SHORT } from '../lib/format';
import { eurC } from '../lib/money';
import { ChipRadios } from './ChipRadios';
import { MahnlaufHinweis } from './MahnlaufHinweis';
import { PdfDialog } from './PdfDialog';
import { darf, GesperrtKnopf, tarifFehler } from './tarifHinweis';
import { MAHN_TITLES, type MahnlaufKurz, type Overview } from './types';
import type { Waehrung } from './waehrung';
import { stundenText } from './zeiten/dauer';
import { UhrKarte, UhrStarten } from './zeiten/Uhr';
import { zeitenText } from './zeiten/ZeitDaten';
import './uebersicht.css';

// Übersicht als Startseite (Funktionsliste Punkt 1): oben die Lage auf einen Blick (offen, überfällig, bezahlt und
// Umsatz im Jahr), darunter Umsatz bzw. Zahlungseingänge je Monat mit dem Vorjahr, überfällige Rechnungen, beste
// Kunden und Objekte, wie lange Kunden zum Zahlen brauchen und offene Angebote. Hinweise oben nur im Sonderfall
// (Kleinunternehmer nahe der Grenze, E-Rechnung bald Pflicht).
// Gerechnet wird auf dem Server (GET /rechnungstool/:clientId/uebersicht), mit denselben Zahlen wie die Rechnungsliste.

interface UeRechnung {
  id: string;
  number: string;
  customer: string;
  kundeId: string | null;
  dueDate: string;
  tage: number;
  // in der Währung der Rechnung
  offen: number;
  waehrung?: Waehrung;
  mahnstufe: number;
}

interface UeDaten {
  jahr: number;
  heute: string;
  jahre: number[];
  rechnungen: number;
  umsatz: { monate: number[]; vorjahr: number[]; summe: number; vorjahrSumme: number; vorjahrBis: { datum: string; summe: number } | null };
  eingaenge: { monate: number[]; vorjahr: number[]; summe: number; anzahl: number };
  offen: { betrag: number; anzahl: number };
  ueberfaellig: { betrag: number; anzahl: number; liste: UeRechnung[] };
  faellig7: { betrag: number; anzahl: number };
  topKunden: { kundeId: string | null; nummer: number | null; name: string; umsatz: number; rechnungen: number }[];
  topObjekte: { site: string; umsatz: number; rechnungen: number; kunde: string; kunden: number }[];
  zahlungsdauer: { tage: number | null; ziel: number | null; rechnungen: number; seit: string };
  // online: in den letzten 7 Tagen über den Link angenommen bzw. abgelehnt
  angebote: { offen: number; betrag: number; abgelaufen: number; online: { angenommen: number; abgelehnt: number } };
  // Auftragsbestätigungen und Lieferscheine, aus denen noch eine Rechnung zu schreiben ist (Betrag brutto)
  auftraege?: { offen: number; betrag: number };
  // wiederkehrende Rechnungen: laufend, angehalten mit Fehler
  abos?: { aktiv: number; fehler: number; hinweis?: number };
  // Mahnläufe: an, Art, pausiert, heute fällig, nicht verschickt (null: nie eingestellt)
  mahnlauf?: MahnlaufKurz | null;
  ku: Overview['ku'];
  // E-Rechnung kommt bald von selbst (server erechnung-an.ts hinweisErechnung), sonst null
  erechnung?: 'bald' | null;
  // Belege in anderer Währung: alle Summen sind zum Kurs der Belege in Euro umgerechnet
  fremd?: boolean;
  // Billtoro: Zeiten in der Sicht des Logins (noch zu berechnen: Minuten, Einträge, Kunden, davon ohne Kunde); null:
  // keine Zeit in der Sicht oder Kanzlei
  zeiten?: { offenMinuten: number; eintraege: number; kunden: number; ohneKunde: number } | null;
  // Billtoro: Artikel mit wenig Bestand (unter dem Mindestbestand oder unter 0); null: kein Artikel führt Bestand oder
  // Kanzlei
  lager?: { wenig: number } | null;
}

const rechnungen = (n: number) => (n === 1 ? '1 Rechnung' : `${n} Rechnungen`);
const tage = (n: number) => (n === 1 ? '1 Tag' : `${n} Tagen`);
// ganze Euro ohne Zeichen für Achsen: „12.500“
const ganz = (v: number) => Math.round(v).toLocaleString('de-DE');

export function Uebersicht({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const [jahr, setJahr] = useState<number | null>(null);
  const [u, setU] = useState<UeDaten | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<UeRechnung | null>(null);
  const [versuch, setVersuch] = useState(0);
  // Billtoro: Tarif-Fehler zeigt das Fenster „Mehr mit Billtoro“, hier dann nichts (kein „Erneut versuchen“)
  const [gesperrt, setGesperrt] = useState(false);
  const firmId = data.firm.id;

  // neu laden bei anderem Jahr, anderer Firma und wenn die Seite ihre Daten neu geladen hat
  useEffect(() => {
    let aktuell = true;
    setBusy(true);
    api
      .get<UeDaten>(`/api/rechnungstool/${firmId}/uebersicht${jahr ? `?jahr=${jahr}` : ''}`)
      .then((d) => {
        if (!aktuell) return;
        setU(d);
        setError(null);
        setGesperrt(false);
      })
      .catch((err: unknown) => {
        if (!aktuell) return;
        if (tarifFehler(err)) setGesperrt(true);
        else setError(errorText(err));
      })
      .finally(() => aktuell && setBusy(false));
    return () => {
      aktuell = false;
    };
  }, [firmId, jahr, data, versuch]);

  return (
    <div className="ue">
      <div className="rt-head">
        <div>
          <h1>Übersicht</h1>
          <p className="muted">
            {data.firm.name} · <span className="ue-nw">Stand {fmtDate(data.today)}</span>
          </p>
        </div>
        {/* Billtoro: nur mit dem Recht (die Kennzahlen sieht jeder Login der Firma) */}
        <div className="rt-head-act">
          {darf(data.tarif, 'angebote') && (
            <Link to="/rechnung/angebote/neu" className="btn">
              + Neues Angebot
            </Link>
          )}
          {darf(data.tarif, 'rechnungen') && (
            <Link to="/rechnung/neu" className="btn primary">
              + Neue Rechnung
            </Link>
          )}
        </div>
      </div>

      {gesperrt && !u ? null : error && !u ? (
        <div className="notice bad ue-err">
          <span>{error}</span>
          <button type="button" className="btn small" onClick={() => setVersuch((n) => n + 1)}>
            Erneut versuchen
          </button>
        </div>
      ) : !u ? (
        <div className="spinner" aria-label="Lädt" />
      ) : u.rechnungen === 0 ? (
        <>
          <ZeitenKarte data={data} zeiten={u.zeiten ?? null} reload={reload} />
          <NutzungHinweis tarif={data.tarif} wo="uebersicht" />
          <RechtsformHinweis absender={data.absender} />
          <KuHinweis ku={u.ku} absender={data.absender} />
          <ErechnungHinweis firmId={firmId} art={u.erechnung} kann={darf(data.tarif, 'einstellungen')} onAn={() => setVersuch((n) => n + 1)} />
          <AboHinweis abos={u.abos} />
          <MahnlaufHinweis mahnlauf={u.mahnlauf} tarif={data.tarif} wo="uebersicht" />
          <LagerHinweis lager={u.lager} />
          <Start tarif={data.tarif} />
        </>
      ) : (
        <div className={`ue-body${busy ? ' busy' : ''}`} aria-busy={busy}>
          <ZeitenKarte data={data} zeiten={u.zeiten ?? null} reload={reload} />
          <NutzungHinweis tarif={data.tarif} wo="uebersicht" />
          <RechtsformHinweis absender={data.absender} />
          <KuHinweis ku={u.ku} absender={data.absender} />
          <ErechnungHinweis firmId={firmId} art={u.erechnung} kann={darf(data.tarif, 'einstellungen')} onAn={() => setVersuch((n) => n + 1)} />
          <AboHinweis abos={u.abos} />
          <MahnlaufHinweis mahnlauf={u.mahnlauf} tarif={data.tarif} wo="uebersicht" />
          <LagerHinweis lager={u.lager} />
          {error && <div className="notice bad">{error}</div>}
          {u.jahre.length > 1 && (
            <div className="ue-filter">
              <span className="ue-filter-l">Jahr</span>
              <ChipRadios
                label="Jahr"
                value={String(jahr ?? u.jahr)}
                options={u.jahre.map((j) => [String(j), String(j)] as const)}
                onChange={(v) => setJahr(Number(v))}
              />
            </div>
          )}
          <Kacheln u={u} />
          {u.fremd && <p className="ue-note ue-fremd">Summen in Euro, Belege in anderer Währung umgerechnet zum Kurs der Belege.</p>}
          <Verlauf key={u.jahr} u={u} />
          <div className="ue-grid">
            <Ueberfaellig u={u} onOpen={setView} />
            <TopKunden u={u} />
            <TopObjekte u={u} />
            {/* die kleinen Karten untereinander neben den Objekten, damit keine Lücke im Raster bleibt */}
            <div className="ue-stack">
              <Zahlungsdauer u={u} />
              <Angebote u={u} neu={darf(data.tarif, 'angebote')} />
            </div>
          </div>
        </div>
      )}

      <PdfDialog
        url={view ? `/api/rechnungstool/rechnung/${view.id}/pdf` : null}
        title={view ? `Rechnung ${view.number}` : ''}
        download={view ? `/api/rechnungstool/rechnung/${view.id}/pdf?download=1` : undefined}
        onClose={() => setView(null)}
      />
    </div>
  );
}

// Billtoro: Karte „Zeiten“ ganz oben, sobald es in der Sicht des Logins eine Zeit gibt oder seine Uhr läuft (wer keine
// Zeiten nutzt, sieht nichts davon): Uhr starten bzw. die laufende Uhr, „+ Zeit eintragen“ und was noch zu berechnen ist
function ZeitenKarte({ data, zeiten, reload }: { data: Overview; zeiten: UeDaten['zeiten'] | null; reload(): Promise<void> }) {
  const uhr = data.zeiten?.uhr ?? null;
  if (!data.zeiten || (!zeiten && !uhr)) return null;
  const tarif = data.tarif;
  const fehlt = fehlendeFunktion(tarif, 'zeiten');
  const offen = zeiten && zeiten.offenMinuten > 0 ? zeiten : null;
  const kunden = offen && offen.kunden > 0 ? ` bei ${offen.kunden === 1 ? '1 Kunden' : `${offen.kunden} Kunden`}` : '';
  const ohne = offen && offen.ohneKunde > 0 ? ` · ${zeitenText(offen.ohneKunde)} ohne Kunde` : '';
  return (
    <section className="rt-card ue-card ue-zeiten" aria-labelledby="ue-zeiten-h">
      <div className="ue-card-head">
        <h2 id="ue-zeiten-h">Zeiten</h2>
        <Link to="/rechnung/zeiten" className="ue-head-link">
          Alle Zeiten
        </Link>
      </div>
      {uhr ? (
        <UhrKarte
          key={uhr.id}
          uhr={uhr}
          kunden={data.kunden}
          klein
          onUhr={(x) => {
            // geändert: die Karte hält ihre Felder selbst; gestoppt oder verworfen: neu laden
            if (!x) void reload();
          }}
          onGestoppt={() => void reload()}
        />
      ) : (
        <div className="ue-zeiten-act">
          <UhrStarten firmId={data.firm.id} tarif={tarif} onUhr={() => void reload()} />
          {fehlt ? (
            <GesperrtKnopf tarif={tarif} fehlt={fehlt} className="btn" onUpgrade={() => upgradeAnbieten(tarif, 'zeiten')}>
              + Zeit eintragen
            </GesperrtKnopf>
          ) : (
            <Link to="/rechnung/zeiten?neu=1" className="btn">
              + Zeit eintragen
            </Link>
          )}
        </div>
      )}
      {offen && (
        <p className="ue-lage ue-zeiten-offen">
          <Link to={darf(tarif, 'rechnungen') ? '/rechnung/zeiten?abrechnen=1' : '/rechnung/zeiten?status=offen&monat=alle'}>
            Noch zu berechnen: {stundenText(offen.offenMinuten)}
            {kunden}
          </Link>
          {ohne}
        </p>
      )}
    </section>
  );
}

// Leere Firma: freundlicher Start. Billtoro ohne Kontoauszug oder Mahnungen im Tarif: nur, was der Tarif enthält
function Start({ tarif }: { tarif: Overview['tarif'] }) {
  const klein = !!fehlendeFunktion(tarif, 'kontoauszug') || !!fehlendeFunktion(tarif, 'mahnungen');
  return (
    <div className="rt-card ue-start">
      <h2>Willkommen bei Ihren Rechnungen</h2>
      <p className="muted">
        Sobald Sie Rechnungen schreiben, sehen Sie hier auf einen Blick, was offen ist, was bezahlt ist und wie sich Ihr Umsatz entwickelt.
      </p>
      <ol className="ue-steps">
        <li className="done">
          <strong>Ihre Angaben sind gespeichert</strong>
          <span>Firma, Steuernummer und Bankverbindung stehen auf jeder Rechnung.</span>
        </li>
        <li>
          <strong>Erste Rechnung schreiben</strong>
          <span>Nummer, Datum und Umsatzsteuer rechnet die Seite für Sie.</span>
        </li>
        <li>
          <strong>Zahlungen im Blick behalten</strong>
          <span>
            {klein
              ? 'Zahlungen eintragen und freundlich erinnern.'
              : 'Zahlungen eintragen oder den Kontoauszug hochladen, bei Bedarf freundlich erinnern und mahnen.'}
          </span>
        </li>
      </ol>
      <div className="ue-start-act">
        {darf(tarif, 'rechnungen') && (
          <Link to="/rechnung/neu" className="btn primary">
            Erste Rechnung schreiben
          </Link>
        )}
        <Link to="/rechnung/kunden" className="linkbtn">
          Oder zuerst Ihre Kunden anlegen
        </Link>
      </div>
    </div>
  );
}

// Kleinunternehmer: Die App zählt still mit, ein Hinweis nur nahe an der Grenze oder wenn der Umsatz im Vorjahr
// darüber lag (dann sperrt der Server neue Rechnungen als Kleinunternehmer). Hat die App das Vorjahr selbst gezählt
// (Zahlungseingänge) oder weiß sie nichts darüber (Grenze 25.000 € wie im ersten Jahr), nennt der Hinweis den Ausweg:
// den Umsatz des Vorjahres in „Meine Angaben“ eintragen (der Knopf öffnet die Seite mit diesem Teil aufgeklappt).
function KuHinweis({ ku, absender }: { ku: UeDaten['ku']; absender: Overview['absender'] }) {
  if (!ku || (!ku.vorjahrUeber && !ku.warnen)) return null;
  const vj = ku.jahr - 1;
  // Umsatz außerhalb dieser Seite, der im Vorjahr angegeben war, zählt mit (wie auf dem Server)
  const ausserhalb = absender.kuJahr === vj && absender.kuExtra > 0;
  return (
    <div className={`notice ${ku.vorjahrUeber ? 'bad' : 'warn'} rt-ku`}>
      <span>
        {ku.vorjahrUeber
          ? ku.vorjahrAngabe
            ? `Ihr Umsatz ${vj} lag über 25.000 €. Damit sind Sie ${ku.jahr} kein Kleinunternehmer mehr.`
            : `Nach den Zahlungen, die ${vj} eingegangen sind, ${ausserhalb ? 'und Ihrem Umsatz außerhalb dieser Seite ' : ''}lag Ihr Umsatz ${vj} bei ${eurC(ku.vorjahr)}, über 25.000 €. Damit sind Sie ${ku.jahr} kein Kleinunternehmer mehr. War Ihr ganzer Umsatz ${vj} niedriger, tragen Sie ihn in Meine Angaben ein.`
          : ku.vorjahrFehlt
            ? `Ihr Umsatz ${ku.jahr} liegt bei ${eurC(ku.umsatz)}, nahe an der Kleinunternehmer-Grenze von ${eurC(ku.grenze)}, die im ersten Jahr gilt. Gab es Ihre Firma schon ${vj}? Dann tragen Sie in Meine Angaben den Umsatz ${vj} insgesamt ein, dann gilt 100.000 €.`
            : `Ihr Umsatz ${ku.jahr} liegt bei ${eurC(ku.umsatz)}, nahe an der Kleinunternehmer-Grenze von ${eurC(ku.grenze)}.`}
      </span>
      {(ku.vorjahrUeber || ku.vorjahrFehlt) && (
        <Link to="/rechnung/angaben#kleinunternehmer" className="btn small">
          Meine Angaben
        </Link>
      )}
    </div>
  );
}

// E-Rechnung nur im Sonderfall: ab Oktober 2027 der Hinweis, dass es ab 2028 von selbst kommt (nicht, wenn sie es
// 2027 schon von selbst ist, server erechnung-an.ts). „Einschalten“ schaltet sie gleich ein (wie in den Einstellungen;
// kann: Billtoro mit dem Recht „Einstellungen“).
function ErechnungHinweis({ firmId, art, kann, onAn }: { firmId: string; art: UeDaten['erechnung']; kann: boolean; onAn(): void }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  if (!art) return null;
  async function einschalten() {
    setBusy(true);
    setFehler(null);
    try {
      await api.put(`/api/rechnungstool/${firmId}/einstellungen/erechnung`, { an: true });
      onAn();
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="notice info rt-ku">
      <span>
        Ab 2028 werden Ihre Rechnungen an Firmen automatisch E-Rechnungen.
        {fehler && ` ${fehler}`}
      </span>
      {kann && (
        <button type="button" className="btn small" disabled={busy} onClick={() => void einschalten()}>
          Einschalten
        </button>
      )}
    </div>
  );
}

// Alte Angaben ohne Rechtsform: Ohne sie fehlen bei GmbH, UG, KG usw. Registergericht und Geschäftsführer auf der Rechnung
function RechtsformHinweis({ absender }: { absender: Overview['absender'] }) {
  if (!absender.saved || absender.rechtsform) return null;
  return (
    <div className="notice warn rt-ku">
      <span>Ihre Rechtsform fehlt noch. Mit ihr stehen die Pflichtangaben unten auf jeder Rechnung.</span>
      <Link to="/rechnung/angaben" className="btn small">
        Ergänzen
      </Link>
    </div>
  );
}

// Wiederkehrende Rechnungen mit Fehler: Es entstehen keine Rechnungen, bis der Mandant sie prüft und fortsetzt. Sonst
// ein Hinweis (z. B. nachgeholte Rechnungen, die nicht per E-Mail gingen).
function AboHinweis({ abos }: { abos: UeDaten['abos'] }) {
  if (!abos?.fehler && !abos?.hinweis) return null;
  return (
    <div className={`notice ${abos.fehler ? 'bad' : 'warn'} rt-ku`}>
      <span>
        {abos.fehler
          ? `${abos.fehler === 1 ? 'Eine wiederkehrende Rechnung ist' : `${abos.fehler} wiederkehrende Rechnungen sind`} wegen eines Fehlers angehalten. Bis Sie sie prüfen und fortsetzen, entstehen daraus keine Rechnungen.`
          : 'Bei wiederkehrenden Rechnungen sind Rechnungen nachgeholt, die nicht per E-Mail gingen. Bitte prüfen und selbst senden.'}
      </span>
      <Link to="/rechnung/wiederkehrend" className="btn small">
        Prüfen
      </Link>
    </div>
  );
}

// Lager: Artikel mit wenig Bestand, Link zur gefilterten Lagerliste
function LagerHinweis({ lager }: { lager: UeDaten['lager'] }) {
  if (!lager?.wenig) return null;
  return (
    <div className="notice warn rt-ku">
      <span>{lager.wenig === 1 ? '1 Artikel' : `${lager.wenig} Artikel`} mit wenig Bestand</span>
      <Link to="/rechnung/lager?filter=wenig" className="btn small">
        Ansehen
      </Link>
    </div>
  );
}

// Vergleich mit dem Vorjahr: „▲ 12 % ggü. 2025 bis 13.10.“
function Vergleich({ u }: { u: UeDaten }) {
  const bis = u.umsatz.vorjahrBis;
  const basis = bis ? bis.summe : u.umsatz.vorjahrSumme;
  const wann = `${u.jahr - 1}${bis ? ` bis ${fmtDate(bis.datum).slice(0, 6)}` : ''}`;
  if (basis <= 0) return <span className="ue-kpi-s">{wann}: kein Umsatz</span>;
  const pct = Math.round(((u.umsatz.summe - basis) / basis) * 100);
  const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return (
    <span className={`ue-kpi-s ue-delta ${dir}`}>
      <span aria-hidden="true">{dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■'}</span> {pct > 0 ? '+' : ''}
      {pct} % ggü. {wann}
    </span>
  );
}

function Kacheln({ u }: { u: UeDaten }) {
  const ue = u.ueberfaellig;
  return (
    <div className="ue-kpis">
      <Link to="/rechnung/rechnungen?filter=offen" className="ue-kpi">
        <span className="ue-kpi-l">Offen</span>
        <span className="ue-kpi-v">{eurC(u.offen.betrag)}</span>
        <span className="ue-kpi-s">{u.offen.anzahl ? rechnungen(u.offen.anzahl) : 'nichts offen'}</span>
        {u.faellig7.anzahl > 0 && <span className="ue-kpi-s">{eurC(u.faellig7.betrag)} in 7 Tagen fällig</span>}
      </Link>
      <Link to="/rechnung/rechnungen?filter=ueberfaellig" className={`ue-kpi${ue.anzahl ? ' bad' : ''}`}>
        <span className="ue-kpi-l">Überfällig</span>
        <span className="ue-kpi-v">{eurC(ue.betrag)}</span>
        <span className="ue-kpi-s">{ue.anzahl ? `${rechnungen(ue.anzahl)} · jetzt mahnen` : 'nichts überfällig'}</span>
      </Link>
      <Link to="/rechnung/rechnungen?filter=bezahlt" className="ue-kpi ok">
        <span className="ue-kpi-l">Bezahlt {u.jahr}</span>
        <span className="ue-kpi-v">{eurC(u.eingaenge.summe)}</span>
        <span className="ue-kpi-s">{u.eingaenge.anzahl ? `Eingänge auf ${rechnungen(u.eingaenge.anzahl)}` : 'noch kein Geldeingang'}</span>
      </Link>
      <Link to="/rechnung/rechnungen" className="ue-kpi">
        <span className="ue-kpi-l">Umsatz {u.jahr} netto</span>
        <span className="ue-kpi-v">{eurC(u.umsatz.summe)}</span>
        <Vergleich u={u} />
      </Link>
    </div>
  );
}

type Art = 'umsatz' | 'eingaenge';
const ARTEN: [Art, string][] = [
  ['umsatz', 'Umsatz'],
  ['eingaenge', 'Zahlungseingänge'],
];

// Achse in runden Schritten (1, 2, 2,5 oder 5 mal 10er-Potenz, mindestens 1 €): der kleinste Schritt, mit dem
// höchstens vier Abschnitte bis zum größten Wert reichen, damit die Balken die Höhe nutzen
function achse(max: number): number[] {
  if (max <= 0) return [0, 100, 200, 300];
  const p = Math.max(1, 10 ** Math.floor(Math.log10(max / 4)));
  const step = [1, 2, 2.5, 5, 10].map((f) => f * p).find((s) => Math.ceil(max / s) <= 4)!;
  return Array.from({ length: Math.ceil(max / step) + 1 }, (_, i) => i * step);
}

// Balken je Monat: das gewählte Jahr kräftig, das Vorjahr blass daneben. Antippen oder darüberfahren zeigt die Werte
// des Monats; dieselben Zahlen stehen in der Tabelle.
function Verlauf({ u }: { u: UeDaten }) {
  const [art, setArt] = useState<Art>('umsatz');
  const laufend = u.jahr === Number(u.heute.slice(0, 4));
  const monatHeute = Number(u.heute.slice(5, 7));
  const [sel, setSel] = useState(laufend ? monatHeute - 1 : 11);
  const reihe = art === 'umsatz' ? u.umsatz : u.eingaenge;
  const vj = u.jahr - 1;
  const ticks = achse(Math.max(...reihe.monate, ...reihe.vorjahr));
  const top = ticks[ticks.length - 1]!;
  const h = (v: number) => `${(Math.max(0, v) / top) * 100}%`;
  const zukunft = (i: number) => laufend && i + 1 > monatHeute;
  const vorjahrSumme = art === 'umsatz' ? u.umsatz.vorjahrSumme : u.eingaenge.vorjahr.reduce((s, v) => s + v, 0);
  const summe = art === 'umsatz' ? u.umsatz.summe : u.eingaenge.summe;
  return (
    <section className="rt-card ue-card ue-verlauf" aria-labelledby="ue-verlauf-h">
      <div className="ue-card-head">
        <div>
          <h2 id="ue-verlauf-h">{art === 'umsatz' ? 'Umsatz je Monat' : 'Zahlungseingänge je Monat'}</h2>
          <p className="ue-sub">
            {art === 'umsatz' ? 'netto nach Rechnungsdatum' : 'Geld nach dem Tag des Eingangs'} · {u.jahr}: <b>{eurC(summe)}</b> · {vj}:{' '}
            {eurC(vorjahrSumme)}
          </p>
        </div>
        <ChipRadios<Art> label="Zeigen" value={art} options={ARTEN} onChange={setArt} className="ue-art" />
      </div>
      <p className="ue-readout" aria-live="polite">
        <b>
          {MONTHS[sel]} {u.jahr}: {zukunft(sel) ? 'noch offen' : eurC(reihe.monate[sel]!)}
        </b>
        <span className="muted">
          {' '}
          · {MONTHS[sel]} {vj}: {eurC(reihe.vorjahr[sel]!)}
        </span>
      </p>
      <div className="ue-chart">
        <div className="ue-axis" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t} style={{ bottom: `${(t / top) * 100}%` }}>
              {ganz(t)}
            </span>
          ))}
        </div>
        <div className="ue-plot" role="group" aria-label={`${art === 'umsatz' ? 'Umsatz' : 'Zahlungseingänge'} je Monat, ${u.jahr} und ${vj}`}>
          <div className="ue-grid-lines" aria-hidden="true">
            {ticks.map((t) => (
              <i key={t} style={{ bottom: `${(t / top) * 100}%` }} />
            ))}
          </div>
          {reihe.monate.map((v, i) => (
            <button
              key={i}
              type="button"
              className={`ue-col${sel === i ? ' on' : ''}${zukunft(i) ? ' future' : ''}`}
              aria-pressed={sel === i}
              aria-label={`${MONTHS[i]}: ${u.jahr} ${zukunft(i) ? 'noch offen' : eurC(v)}, ${vj} ${eurC(reihe.vorjahr[i]!)}`}
              onClick={() => setSel(i)}
              onPointerEnter={(e) => e.pointerType === 'mouse' && setSel(i)}
              onFocus={() => setSel(i)}
            >
              <span className="ue-bars">
                <span className="ue-bar vj" style={{ height: h(reihe.vorjahr[i]!) }} />
                <span className="ue-bar" style={{ height: zukunft(i) ? 0 : h(v) }} />
              </span>
              <span className="ue-m">
                <span className="ue-m-l">{MONTHS_SHORT[i]}</span>
                <span className="ue-m-s">{MONTHS_SHORT[i]!.slice(0, 1)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="ue-foot">
        <div className="ue-legend">
          <span>
            <i className="ue-key" /> {u.jahr}
          </span>
          <span>
            <i className="ue-key vj" /> {vj}
          </span>
        </div>
        <details className="ue-tbl">
          <summary>Als Tabelle</summary>
          <div className="ue-tbl-w">
            <table>
              <thead>
                <tr>
                  <th>Monat</th>
                  <th className="r">{u.jahr}</th>
                  <th className="r">{vj}</th>
                </tr>
              </thead>
              <tbody>
                {reihe.monate.map((v, i) => (
                  <tr key={i}>
                    <td>{MONTHS[i]}</td>
                    <td className="r num">{zukunft(i) ? '–' : eurC(v)}</td>
                    <td className="r num">{eurC(reihe.vorjahr[i]!)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Summe</td>
                  <td className="r num">{eurC(summe)}</td>
                  <td className="r num">{eurC(vorjahrSumme)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </details>
      </div>
    </section>
  );
}

function Ueberfaellig({ u, onOpen }: { u: UeDaten; onOpen(r: UeRechnung): void }) {
  const ue = u.ueberfaellig;
  const f7 = u.faellig7;
  const mehr = ue.anzahl - ue.liste.length;
  return (
    <section className="rt-card ue-card ue-ueber" aria-labelledby="ue-ueber-h">
      <div className="ue-card-head">
        <h2 id="ue-ueber-h">Überfällig</h2>
        {ue.anzahl > 0 && <span className="badge b-bad">{rechnungen(ue.anzahl)}</span>}
      </div>
      {ue.anzahl === 0 ? (
        <p className="ue-ok">
          <span className="ue-ok-i" aria-hidden="true">
            ✓
          </span>
          Keine Rechnung ist überfällig.
        </p>
      ) : (
        <ul className="ue-rows">
          {ue.liste.map((r) => (
            <li key={r.id}>
              <button type="button" className="ue-row" onClick={() => onOpen(r)} aria-label={`Rechnung ${r.number} an ${r.customer} ansehen`}>
                <span className="ue-row-t">
                  <strong>{r.customer}</strong>
                  <span className="muted">
                    <span className="num">{r.number}</span> · fällig am {fmtDate(r.dueDate)}
                  </span>
                </span>
                <span className="ue-row-v">
                  <strong className="num">{eurC(r.offen, r.waehrung)}</strong>
                  <span className="ue-bad">{r.mahnstufe ? MAHN_TITLES[r.mahnstufe - 1] : `seit ${tage(r.tage)}`}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {f7.anzahl > 0 && (
        <p className="ue-note">
          In den nächsten 7 Tagen {f7.anzahl === 1 ? 'wird 1 Rechnung' : `werden ${f7.anzahl} Rechnungen`} über {eurC(f7.betrag)} fällig.
        </p>
      )}
      {ue.anzahl > 0 && (
        <Link to="/rechnung/rechnungen?filter=ueberfaellig" className="ue-more">
          {mehr > 0 ? `Alle ${ue.anzahl} überfälligen ansehen und mahnen` : 'In der Liste ansehen und mahnen'} →
        </Link>
      )}
    </section>
  );
}

// Rangliste mit dünnem Balken (Anteil am Besten), Betrag rechts
function Rang({ items }: { items: { key: string; name: ReactNode; info: string; wert: number }[] }) {
  const max = Math.max(...items.map((i) => i.wert), 0) || 1;
  return (
    <ol className="ue-rang">
      {items.map((i) => (
        <li key={i.key}>
          <div className="ue-rang-t">
            <span className="ue-rang-n">{i.name}</span>
            <strong className="num">{eurC(i.wert)}</strong>
          </div>
          <span className="ue-rang-bar" aria-hidden="true">
            <i style={{ width: `${(i.wert / max) * 100}%` }} />
          </span>
          <span className="ue-rang-i muted">{i.info}</span>
        </li>
      ))}
    </ol>
  );
}

const anteil = (v: number, summe: number) => (summe > 0 ? `${Math.round((v / summe) * 100)} % vom Umsatz` : '');

function TopKunden({ u }: { u: UeDaten }) {
  return (
    <section className="rt-card ue-card" aria-labelledby="ue-kunden-h">
      <div className="ue-card-head">
        <h2 id="ue-kunden-h">Beste Kunden {u.jahr}</h2>
        <Link to="/rechnung/kunden" className="ue-head-link">
          Alle Kunden
        </Link>
      </div>
      {u.topKunden.length === 0 ? (
        <p className="ue-empty">Noch kein Umsatz in {u.jahr}.</p>
      ) : (
        <Rang
          items={u.topKunden.map((k) => ({
            key: k.kundeId ?? `name-${k.name}`,
            name: k.kundeId ? <Link to={`/rechnung/kunden/${k.kundeId}`}>{k.name}</Link> : k.name,
            info: [rechnungen(k.rechnungen), anteil(k.umsatz, u.umsatz.summe)].filter(Boolean).join(' · '),
            wert: k.umsatz,
          }))}
        />
      )}
    </section>
  );
}

function TopObjekte({ u }: { u: UeDaten }) {
  return (
    <section className="rt-card ue-card" aria-labelledby="ue-objekte-h">
      <div className="ue-card-head">
        <h2 id="ue-objekte-h">Objekte {u.jahr}</h2>
      </div>
      {u.topObjekte.length === 0 ? (
        <p className="ue-empty">
          Noch keine Rechnung mit Objekt oder Einsatzort in {u.jahr}. Tragen Sie ihn beim Schreiben ein, dann sehen Sie hier, welches Objekt wie viel
          bringt.
        </p>
      ) : (
        <Rang
          items={u.topObjekte.map((o) => ({
            key: o.site,
            name: o.site,
            info: [o.kunden > 1 ? `${o.kunden} Kunden` : o.kunde, rechnungen(o.rechnungen)].join(' · '),
            wert: o.umsatz,
          }))}
        />
      )}
    </section>
  );
}

// Ø Tage bis zur Zahlung, mit dem Zahlungsziel als Strich auf derselben Skala
function Zahlungsdauer({ u }: { u: UeDaten }) {
  const d = u.zahlungsdauer;
  if (d.tage === null || d.ziel === null) {
    return (
      <section className="rt-card ue-card" aria-labelledby="ue-dauer-h">
        <div className="ue-card-head">
          <h2 id="ue-dauer-h">Zahlungsdauer</h2>
        </div>
        <p className="ue-empty">Noch keine bezahlte Rechnung in den letzten 12 Monaten.</p>
      </section>
    );
  }
  const spaet = d.tage - d.ziel;
  const lage = spaet > 7 ? 'bad' : spaet > 0 ? 'warn' : 'ok';
  const skala = Math.max(d.tage, d.ziel, 1) * 1.25;
  return (
    <section className="rt-card ue-card ue-dauer" aria-labelledby="ue-dauer-h">
      <div className="ue-card-head">
        <h2 id="ue-dauer-h">Zahlungsdauer</h2>
      </div>
      <p className="ue-big">
        {d.tage} {d.tage === 1 ? 'Tag' : 'Tage'}
      </p>
      <p className="ue-sub">im Schnitt vom Rechnungsdatum bis zum Geld</p>
      <div className="ue-meter" aria-hidden="true">
        <i className={lage} style={{ width: `${(d.tage / skala) * 100}%` }} />
        <b style={{ left: `${(d.ziel / skala) * 100}%` }} />
      </div>
      <p className={`ue-lage ${lage}`}>
        {spaet > 0
          ? `Ihre Kunden zahlen im Schnitt ${spaet} ${spaet === 1 ? 'Tag' : 'Tage'} nach dem Zahlungsziel (Ø ${d.ziel} ${d.ziel === 1 ? 'Tag' : 'Tage'}).`
          : `Pünktlich: Das Zahlungsziel ist im Schnitt ${d.ziel} ${d.ziel === 1 ? 'Tag' : 'Tage'}.`}
      </p>
      <p className="ue-note">
        Seit {fmtDate(d.seit)}, {rechnungen(d.rechnungen)} ganz bezahlt
      </p>
    </section>
  );
}

function Angebote({ u, neu }: { u: UeDaten; neu: boolean }) {
  const a = u.angebote;
  const zu = u.auftraege ?? { offen: 0, betrag: 0 };
  return (
    <section className="rt-card ue-card" aria-labelledby="ue-angebote-h">
      <div className="ue-card-head">
        <h2 id="ue-angebote-h">Angebote</h2>
        <Link to="/rechnung/angebote" className="ue-head-link">
          Alle Angebote
        </Link>
      </div>
      {a.offen === 0 && a.abgelaufen === 0 && a.online.angenommen === 0 && a.online.abgelehnt === 0 && zu.offen === 0 ? (
        <p className="ue-empty">Keine offenen Angebote.</p>
      ) : (
        <>
          {(a.offen > 0 || a.abgelaufen > 0) && <p className="ue-big">{a.offen === 1 ? '1 offenes Angebot' : `${a.offen} offene Angebote`}</p>}
          {a.offen > 0 && <p className="ue-sub">zusammen {eurC(a.betrag)} netto</p>}
          {a.abgelaufen > 0 && (
            <p className="ue-lage warn">
              <Link to="/rechnung/angebote?filter=abgelaufen">
                {a.abgelaufen === 1 ? '1 Angebot ist abgelaufen' : `${a.abgelaufen} Angebote sind abgelaufen`}
              </Link>
              : nachfassen oder ablehnen.
            </p>
          )}
          {/* Antworten der Kunden über den Link aus der E-Mail */}
          {a.online.angenommen > 0 && (
            <p className="ue-lage ok">
              <Link to="/rechnung/angebote?filter=angenommen">
                {a.online.angenommen === 1 ? '1 Angebot' : `${a.online.angenommen} Angebote`} in den letzten 7 Tagen online angenommen
              </Link>
            </p>
          )}
          {a.online.abgelehnt > 0 && <p className="ue-sub">{a.online.abgelehnt} online abgelehnt</p>}
          {/* Aufträge und Lieferscheine ohne Rechnung (nicht im Umsatz) */}
          {zu.offen > 0 && (
            <p className="ue-lage">
              <Link to="/rechnung/auftraege?filter=offen">
                Noch zu berechnen: {zu.offen === 1 ? '1 Auftrag' : `${zu.offen} Aufträge`} · {eurC(zu.betrag)}
              </Link>
            </p>
          )}
        </>
      )}
      {neu && (
        <Link to="/rechnung/angebote/neu" className="ue-more">
          + Neues Angebot
        </Link>
      )}
    </section>
  );
}
